if (!customElements.get("m-brand-list")) {
  class MBrandList extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      const enableSlider = this.dataset.enableSlider === "true";

      // Always init tooltips
      this._initTooltips();

      if (enableSlider) {
        const wrapper = this.querySelector("[data-wrapper]");
        if (wrapper) wrapper.classList.add("swiper-wrapper");
        requestAnimationFrame(() => this.initSlider());
      }
    }

    _initTooltips() {
      const logos = this.querySelectorAll(".m-brands-list__logo");

      logos.forEach((logo) => {
        // Support both: explicit class OR fallback to any div child with text
        let titleEl =
          logo.querySelector(".m-brands-logo__title") ||
          logo.querySelector("div");

        if (!titleEl) return;

        const title = titleEl.textContent.trim();
        if (!title) return;

        // Hide the original text
        titleEl.style.display = "none";

        // Build tooltip
        const tooltip = document.createElement("span");
        tooltip.className = "m-brands-logo__tooltip";
        tooltip.setAttribute("role", "tooltip");
        tooltip.textContent = title;

        logo.style.position = "relative";
        logo.appendChild(tooltip);
        logo.classList.add("m-brands-logo--has-tooltip");
      });
    }

    initSlider() {
      const items = parseInt(this.dataset.items) || 4;
      const slideContainer = this.querySelector(".swiper-container");
      if (!slideContainer) return;

      const wrapper = slideContainer.querySelector("[data-wrapper]");
      if (!wrapper) return;

      const slides = Array.from(wrapper.children);
      const containerWidth = slideContainer.offsetWidth;
      const slideWidth = containerWidth / items;

      slides.forEach((slide) => {
        slide.style.width = `${slideWidth}px`;
        slide.style.flexShrink = "0";
      });

      // Duplicate slides for seamless loop
      slides.forEach((slide) => {
        const clone = slide.cloneNode(true);
        clone.setAttribute("aria-hidden", "true");
        wrapper.appendChild(clone);
      });

      const speed = parseInt(this.dataset.autoplaySpeed) || 3;
      const duration = slides.length * speed;
      wrapper.style.setProperty("--marquee-duration", `${duration}s`);
      wrapper.classList.add("m-brands-marquee");

      this._initMarqueeDrag(wrapper, slideContainer);
    }

    _initMarqueeDrag(wrapper, container) {
      let isDown = false;
      let isDragging = false;
      let startX = 0;
      let capturedX = 0;

      const getLiveTranslateX = () => {
        const style = window.getComputedStyle(wrapper);
        const matrix = new DOMMatrixReadOnly(style.transform);
        return matrix.m41;
      };

      const startDrag = (clientX) => {
        isDown = true;
        isDragging = false;
        capturedX = getLiveTranslateX();
        startX = clientX;

        // Freeze current position and disable CSS animation
        wrapper.style.animation = "none";
        wrapper.style.transform = `translateX(${capturedX}px)`;
        container.classList.add("is-dragging");
      };

      const onMove = (clientX) => {
        if (!isDown) return;
        const delta = clientX - startX;
        if (Math.abs(delta) > 3) isDragging = true;
        if (!isDragging) return;

        const halfWidth = wrapper.scrollWidth / 2;
        let next = capturedX + delta;
        // Wrap
        if (next > 0) next -= halfWidth;
        if (next < -halfWidth) next += halfWidth;

        wrapper.style.transform = `translateX(${next}px)`;
      };

      const endDrag = () => {
        if (!isDown) return;
        isDown = false;
        container.classList.remove("is-dragging");

        // Block accidental link clicks after a drag
        if (isDragging) {
          container.querySelectorAll("a").forEach((a) => {
            const block = (e) => {
              e.preventDefault();
              a.removeEventListener("click", block);
            };
            a.addEventListener("click", block);
          });
        }

        // Resume animation from current position
        setTimeout(() => {
          const curX = getLiveTranslateX();
          const halfWidth = wrapper.scrollWidth / 2;
          let norm = curX % halfWidth;
          if (norm > 0) norm -= halfWidth;

          const totalDuration =
            parseFloat(
              wrapper.style.getPropertyValue("--marquee-duration") ||
              getComputedStyle(wrapper).getPropertyValue("--marquee-duration")
            ) || 20;

          const fraction = Math.abs(norm) / halfWidth;
          const delay = -(fraction * totalDuration);

          wrapper.style.transform = "";
          wrapper.style.animation = `m-brands-marquee ${totalDuration}s ${delay}s linear infinite`;
        }, 50);
      };

      // Mouse events
      container.addEventListener("mousedown", (e) => startDrag(e.clientX));
      window.addEventListener("mousemove", (e) => onMove(e.clientX));
      window.addEventListener("mouseup", endDrag);

      // Touch events
      container.addEventListener("touchstart", (e) => startDrag(e.touches[0].clientX), { passive: true });
      container.addEventListener("touchmove", (e) => onMove(e.touches[0].clientX), { passive: true });
      container.addEventListener("touchend", endDrag);
    }
  }

  customElements.define("m-brand-list", MBrandList);
}